// Vault deep-link resolution.
//
// The AI chat embeds links like `https://app.eleplan.com/vault-link/<code>`.
// The code is an opaque, short-lived pointer (no decryptable data). When tapped
// in-app — whether from a chat message (MarkdownBody) or an OS universal link
// (RootNavigator) — we resolve it via the authenticated /links/resolve-vault
// endpoint and open that credential's sheet in place over the current screen.
// The value itself is only ever revealed on-device behind biometric, in the
// Vault sheet. We never open the raw URL in a browser.
import { apiGet } from './apiClient';
import { getSessionIds } from '../store/actions/_sessionIds';
import { navigationRef } from '../navigation/navigationRef';
import { store } from '../store';
import { showNotification } from '../store/actions/auth';

// Matches `/vault-link/<code>` on any of our hosts (and the custom scheme).
const VAULT_LINK_RE = /\/vault-link\/([A-Za-z0-9_-]+)/i;

export function extractVaultLinkCode(url?: string | null): string | null {
  if (!url) return null;
  const m = String(url).match(VAULT_LINK_RE);
  return m ? m[1] : null;
}

export function isVaultLinkUrl(url?: string | null): boolean {
  return !!extractVaultLinkCode(url);
}

/**
 * Resolve a vault-link code to { projectId, credentialId } via the
 * authenticated, owner-scoped endpoint, then navigate to the project's Vault
 * with the credentialId so the screen auto-opens that item's sheet.
 *
 * Safe to call from anywhere (chat tap, universal link). Surfaces a toast on
 * expiry / not-found rather than throwing.
 */
export async function resolveAndOpenVaultLink(code: string): Promise<void> {
  if (!code) return;
  try {
    const { accountId, cognitoId } = getSessionIds();
    if (!accountId || !cognitoId) {
      store.dispatch(showNotification('error', 'Vault link', 'Sign in to open this vault item.') as any);
      return;
    }
    const res = await apiGet(`/links/resolve-vault/${accountId}/${cognitoId}/${encodeURIComponent(code)}`);
    if (!res?.success || !res?.payload) {
      store.dispatch(showNotification('error', 'Vault link', res?.message || 'This vault link has expired.') as any);
      return;
    }
    const { project_id: projectId, credential_id: credentialId } = res.payload;
    if (navigationRef.isReady()) {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: 'ProjectVault', params: { projectId, credentialId } },
      });
    }
  } catch (_err) {
    store.dispatch(showNotification('error', 'Vault link', 'Could not open the vault item.') as any);
  }
}
